// src/composables/useQuotas.js
import { ref, computed } from 'vue'
import { useCongeStore } from '@/store/conges'
import { storeToRefs } from 'pinia'
import api from '@/services/api'

export function useQuotas() {
  const congeStore = useCongeStore()
  const { quotas } = storeToRefs(congeStore)
  
  const loading = ref(false)
  const error = ref(null)
  
  /**
   * Récupère les quotas de congés avec pagination et filtres
   * @param {Object} params - Paramètres de filtrage et pagination
   * @returns {Object} - Objet contenant les items, totalItems et totalPages
   */
  const getQuotas = async (params = {}) => {
    loading.value = true
    try {
      const response = await api.getCongeQuotas(params)
      error.value = null
      return {
        items: response.data.data,
        totalItems: response.data.meta.total,
        totalPages: Math.ceil(response.data.meta.total / (params.limit || 10))
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des quotas:', err)
      error.value = err.response?.data?.message || 'Erreur lors de la récupération des quotas'
      return { items: [], totalItems: 0, totalPages: 1 }
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Récupère les quotas de congés et les stocke dans le store
   * @param {Object} params - Paramètres de filtrage (personnelId, typeCongeId, annee)
   */
  const fetchQuotas = async (params = {}) => {
    loading.value = true
    try {
      await congeStore.fetchQuotas(params)
      error.value = null
    } catch (err) {
      console.error('Erreur lors de la récupération des quotas:', err)
      error.value = err.response?.data?.message || 'Erreur lors de la récupération des quotas'
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Récupère le quota d'un agent pour un type de congé spécifique
   * @param {number} personnelId - ID de l'agent
   * @param {number} typeCongeId - ID du type de congé
   * @param {number} annee - Année du quota (par défaut, année courante)
   * @returns {Object|null} - Objet quota ou null si non trouvé
   */
  const getQuotaAgent = async (personnelId, typeCongeId, annee = new Date().getFullYear()) => {
    loading.value = true
    try {
      const response = await api.getCongeQuota(personnelId, typeCongeId, annee)
      error.value = null
      return response.data.data
    } catch (err) {
      console.error('Erreur lors de la récupération du quota:', err)
      error.value = err.response?.data?.message || 'Erreur lors de la récupération du quota'
      return null
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Met à jour le quota d'un agent
   * @param {number} quotaId - ID du quota à mettre à jour
   * @param {Object} quotaData - Données du quota à mettre à jour
   * @returns {Object|null} - Objet quota mis à jour ou null en cas d'erreur
   */
  const updateQuota = async (quotaId, quotaData) => {
    loading.value = true
    try {
      const response = await api.updateCongeQuota(quotaId, quotaData)
      // Mettre à jour le quota dans le store
      await congeStore.fetchQuotas()
      error.value = null
      return response.data.data
    } catch (err) {
      console.error('Erreur lors de la mise à jour du quota:', err)
      error.value = err.response?.data?.message || 'Erreur lors de la mise à jour du quota'
      return null
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Crée un nouveau quota pour un agent
   * @param {Object} quotaData - Données du quota à créer
   * @returns {Object|null} - Objet quota créé ou null en cas d'erreur
   */
  const createQuota = async (quotaData) => {
    loading.value = true
    try {
      const response = await api.createCongeQuota(quotaData)
      // Mettre à jour les quotas dans le store
      await congeStore.fetchQuotas()
      error.value = null
      return response.data.data
    } catch (err) {
      console.error('Erreur lors de la création du quota:', err)
      error.value = err.response?.data?.message || 'Erreur lors de la création du quota'
      return null
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Vérifie si un agent a suffisamment de quota pour une demande de congé
   * @param {number} personnelId - ID de l'agent
   * @param {number} typeCongeId - ID du type de congé
   * @param {number} duree - Durée du congé en jours
   * @param {number} annee - Année du quota (par défaut, année courante)
   * @returns {Object} - Résultat de la vérification { suffisant: boolean, disponible: number, manquant: number }
   */
  const verifierQuotaSuffisant = async (personnelId, typeCongeId, duree, annee = new Date().getFullYear()) => {
    const quota = await getQuotaAgent(personnelId, typeCongeId, annee)
    
    if (!quota) {
      return {
        suffisant: false,
        disponible: 0,
        manquant: duree,
        quotaExiste: false
      }
    }
    
    const disponible = quota.quotaTotal + quota.quotaReporte - quota.quotaUtilise
    const suffisant = disponible >= duree
    const manquant = suffisant ? 0 : duree - disponible
    
    return {
      suffisant,
      disponible,
      manquant,
      quotaExiste: true,
      quota
    }
  }
  
  /**
   * Calcule les statistiques d'utilisation des quotas par type de congé
   * @param {Array} quotasData - Liste des quotas
   * @returns {Array} - Statistiques par type de congé
   */
  const calculerStatsQuotas = (quotasData) => {
    const stats = []
    const typeCongeMap = new Map()
    
    // Regrouper les quotas par type de congé
    quotasData.forEach(quota => {
      const typeId = quota.typeCongeId
      if (!typeCongeMap.has(typeId)) {
        typeCongeMap.set(typeId, {
          typeCongeId: typeId,
          typeCongeDetails: quota.typeCongeDetails,
          totalQuota: 0,
          totalUtilise: 0,
          totalReporte: 0,
          count: 0
        })
      }
      
      const typeStats = typeCongeMap.get(typeId)
      typeStats.totalQuota += quota.quotaTotal
      typeStats.totalUtilise += quota.quotaUtilise
      typeStats.totalReporte += quota.quotaReporte
      typeStats.count++
    })
    
    // Convertir la Map en tableau et calculer les pourcentages
    typeCongeMap.forEach(stat => {
      const disponible = stat.totalQuota + stat.totalReporte - stat.totalUtilise
      const pourcentageUtilisation = stat.totalQuota > 0 
        ? Math.round((stat.totalUtilise / stat.totalQuota) * 100) 
        : 0
      
      stats.push({
        ...stat,
        disponible,
        pourcentageUtilisation
      })
    })
    
    return stats
  }
  
  /**
   * Supprime un quota
   * @param {number} quotaId - ID du quota à supprimer
   * @returns {boolean} - true si supprimé avec succès, false sinon
   */
  const deleteQuota = async (quotaId) => {
    loading.value = true
    try {
      await api.deleteCongeQuota(quotaId)
      // Mettre à jour les quotas dans le store
      await congeStore.fetchQuotas()
      error.value = null
      return true
    } catch (err) {
      console.error('Erreur lors de la suppression du quota:', err)
      error.value = err.response?.data?.message || 'Erreur lors de la suppression du quota'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Exporte les quotas au format CSV ou Excel
   * @param {string} format - Format d'export ('csv' ou 'excel')
   * @param {Object} filters - Filtres à appliquer pour l'export
   */
  const exportQuotas = async (format = 'csv', filters = {}) => {
    loading.value = true
    try {
      const response = await api.exportCongeQuotas(format, filters)
      
      // Créer un lien pour télécharger le fichier
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      
      // Définir le nom du fichier
      const date = new Date().toISOString().split('T')[0]
      const extension = format === 'csv' ? 'csv' : 'xlsx'
      link.setAttribute('download', `quotas_conges_${date}.${extension}`)
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      return true
    } catch (err) {
      console.error(`Erreur lors de l'export des quotas en ${format}:`, err)
      error.value = err.response?.data?.message || `Erreur lors de l'export des quotas en ${format}`
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    quotas,
    loading,
    error,
    getQuotas,
    fetchQuotas,
    getQuotaAgent,
    updateQuota,
    createQuota,
    deleteQuota,
    verifierQuotaSuffisant,
    calculerStatsQuotas,
    exportQuotas
  }
}
